import { Box, keyframes, Typography, useTheme } from '@mui/material';
import { Container } from '../atomic';

/**
 * @typedef {Object} CompanyLogo
 * @property {string} id - Unique identifier
 * @property {string} name - Company name
 * @property {string} [logoSrc] - Optional logo image source
 */

/**
 * @typedef {Object} SocialProofBarProps
 * @property {CompanyLogo[]} [logos] - Array of company logos/names
 * @property {string} [headingText] - Text above the logos
 */

/** Marquee animation keyframes */
const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

/**
 * Social Proof Bar with logo marquee
 * Inspired by Mercury + Stripe
 *
 * @param {SocialProofBarProps} props - Component props
 * @returns {JSX.Element} Social proof bar component
 */
function SocialProofBar({
  logos = [
    { id: '1', name: 'Acme Corp' },
    { id: '2', name: 'Globex Inc' },
    { id: '3', name: 'Initech' },
    { id: '4', name: 'Hooli' },
    { id: '5', name: 'Pied Piper' },
    { id: '6', name: 'Dunder Mifflin' },
  ],
  headingText = '',
}) {
  const theme = useTheme();
  /**
   * Render a single logo placeholder
   * @param {CompanyLogo} logo - Logo data
   * @param {number} index - Logo index
   * @param {string} suffix - Key suffix for duplicate sets
   * @returns {JSX.Element} Logo placeholder component
   */
  const renderLogoPlaceholder = (logo, index, suffix = '') => (
    <Box
      key={`${logo.id}-${suffix}-${index}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '140px',
        height: '44px',
        px: 4,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        cursor: 'default',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      {logo.logoSrc
        ? <Box
            component="img"
            src={logo.logoSrc}
            alt={`${logo.name} logo`}
            sx={{
              maxHeight: '28px',
              maxWidth: '100px',
              filter: 'grayscale(100%)',
              opacity: 0.6,
              transition: 'all 0.3s ease',
              '&:hover': {
                filter: 'grayscale(0%)',
                opacity: 1,
              },
            }}
          />
        : <Typography
            variant="body2"
            sx={{
              color: '#6c757d',
              fontWeight: 600,
              fontSize: '0.9rem',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
            }}
          >
            {logo.name}
          </Typography>}
    </Box>
  );

  return (
    <Box
      component="section"
      aria-label="Trusted by companies"
      sx={{
        py: { xs: 4, md: 5 },
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
      }}
    >
      {headingText && (
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: '#6c757d',
              fontWeight: 500,
              fontSize: '0.95rem',
              mb: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {headingText}
          </Typography>
        </Container>
      )}

      {/* Logo Marquee */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: 'fit-content',
            animation: `${scrollAnimation} 30s linear infinite`,
            '&:hover': {
              animationPlayState: 'paused',
            },
          }}
        >
          {/* First set */}
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              pr: 4,
            }}
          >
            {logos.map((logo, index) =>
              renderLogoPlaceholder(logo, index, 'first')
            )}
          </Box>
          {/* Duplicate set for seamless loop */}
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              pr: 4,
            }}
          >
            {logos.map((logo, index) =>
              renderLogoPlaceholder(logo, index, 'second')
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SocialProofBar;
